import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

export function getMonthYear(period) {
    if (!period) return { monthName: '', year: '', prevYear: '', monthNum: 0 };
    const periodStr = period.toString().padStart(6, '0');
    if (periodStr.length !== 6) return { monthName: '', year: '', prevYear: '', monthNum: 0 };
    const year = periodStr.substring(0, 4);
    const prevYear = (parseInt(year, 10) - 1).toString();
    const month = periodStr.substring(4, 6);
    const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];
    const monthIndex = parseInt(month, 10);
    return {
        monthName: monthNames[monthIndex] || '',
        year,
        prevYear,
        monthNum: monthIndex
    };
}

const useExecutiveDashboardData = () => {
    const { apiFetch } = useAuth();
    const [period, setPeriod] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState([]);
    const [propertyData, setPropertyData] = useState(null);
    const [totalArea, setTotalArea] = useState(0);
    const [condensedIncomeData, setCondensedIncomeData] = useState(null);
    const [condensedIncomeDataPrev, setCondensedIncomeDataPrev] = useState(null);
    const [rrerData, setRrerData] = useState(null);
    const [totalOccupiedGLA, setTotalOccupiedGLA] = useState(0);
    const [totalOccupiedGLAPrev, setTotalOccupiedGLAPrev] = useState(0);
    const [prevTotalGLA, setPrevTotalGLA] = useState("-");
    const [portfolioEscalation, setPortfolioEscalation] = useState(null);
    const [portfolioEscalationProperties, setPortfolioEscalationProperties] = useState([]);

    //Helper to standardize fetch with error handeling
    const safeFetch = async (url, onSuccess, errorsList, label) => {
        try {
            const response = await apiFetch(url);
            const json = await response.json();
            if (json.success !== undefined && !json.success) {
                errorsList.push(`${label}: ${json.message || 'Request failed'}`);
                return;
            }
            onSuccess(json.data !== undefined ? json.data : json);
        } catch (e) {
            if (e.message !== 'Token refresh failed') {
                errorsList.push(`${label}: ${e.message}`);
            }
        }
    };

    useEffect(() => {
        let cancelled = false;
        const fetchDefaultPeriod = async () => {
            try {
                const response = await apiFetch('/api/Mda/DefaultPeriod');
                const data = await response.json();
                if (!cancelled) setPeriod(data.period1);
            } catch (e) {
                if (e.message !== 'Token refresh failed') {
                    console.error('Failed to fetch default period:', e);
                }
            }
        };
        fetchDefaultPeriod();
        return () => { cancelled = true; };
    }, [apiFetch]);

    const fetchAllData = useCallback(async () => {
        if (!period) return;

        setLoading(true);
        setErrors([]);
        const errorsList = [];

        const { prevYear } = getMonthYear(period);
        const prevDecember = `${prevYear}12`;

        await Promise.allSettled([
            //Possible refactor: Sum GLA in backend and return total amount instead of calculating in frontend.
            (async () => {
                const label = 'Property Data';
                try {
                    const response = await apiFetch(`/api/Mda/PropertyData?Period=${period}`);
                    const json = await response.json();
                    if (json.success) {
                        setPropertyData(json.data);
                        const sum = Object.entries(json.data).reduce((acc, [name, area]) => {
                            if (name === 'Unity Centre') return acc;
                            const num = Number(area);
                            return acc + (isNaN(num) ? 0 : num);
                        }, 0);
                        setTotalArea(Math.ceil(sum));
                    } else {
                        errorsList.push(`${label} : ${json.message || 'Failed to fetch'}`);
                    }
                } catch (e) {
                    if (e.message !== 'Token refresh failed') {
                        errorsList.push(`${label}: ${e.message}`);
                    }
                }
            })(),

            safeFetch(`/api/Mda/CondensedIncomeStatementData?Period=${period}`,
                setCondensedIncomeData,
                errorsList,
                'Condensed Income Statement Data (Current)'
            ),

            safeFetch(`/api/Mda/CondensedIncomeStatementData?Period=${prevDecember}`,
                setCondensedIncomeDataPrev,
                errorsList,
                'Condensed Income Statement Data (Previous)'
            ),

            safeFetch(`/api/Mda/RRERatiosFromTenancyData?Period=${period}`,
                setRrerData,
                errorsList,
                'RRER Ratios'
            ),

            safeFetch(
                `/api/Mda/TotalRentableAreaForPeriod?Period=${period}`,
                setPrevTotalGLA,
                errorsList,
                'Total Rentable Area'
            ),

            //Possible refactor: Sum GLA in backend and return total amount instead of calculating in frontend.
            (async () => {
                try {
                    const response = await apiFetch(`/api/Mda/OccupiedGLA?Period=${period}`);
                    const json = await response.json();
                    const sum = Object.entries(json.data)
                        .filter(([name]) => name !== 'Unity Centre')
                        .reduce((acc, [, val]) => acc + (Number(val.occupiedGLA) || 0), 0);

                    setTotalOccupiedGLA(sum);
                } catch (e) {
                    if (e.message !== 'Token refresh failed') {
                        errorsList.push(`Occupied GLA: ${e.message}`);
                    }
                }
            })(),

            safeFetch(`/api/Mda/OccupiedGLAForPreviousPeriod?Period=${period}`,
                setTotalOccupiedGLAPrev,
                errorsList,
                'Occupied GLA (Previous)'
            ),

            (async () => {
                try {
                    const response = await apiFetch(`/api/Mda/PortfolioEscalation?period=${period}`);
                    const json = await response.json();
                    if (json.success && json.data) {
                        setPortfolioEscalation(json.data.portfolioTotalEscalationPercentage);
                        setPortfolioEscalationProperties(json.data.properties || []);
                    }
                } catch (e) {
                    if (e.message !== 'Token refresh failed') {
                        errorsList.push(`Portfolio Escalation: ${e.message}`);
                    }
                }
            })(),
        ]);

        setErrors(errorsList);
        setLoading(false);
    }, [period, apiFetch]);

    useEffect(() => {
        (async () => {
            await fetchAllData();
        })();
    }, [fetchAllData]);

    return {
        // Period
        period,
        setPeriod,
        ...getMonthYear(period),

        // Loading & errors
        loading,
        errors,
        hasErrors: errors.length > 0,

        // Data
        propertyData,
        totalArea,
        condensedIncomeData,
        condensedIncomeDataPrev,
        rrerData,
        totalOccupiedGLA,
        totalOccupiedGLAPrev,
        prevTotalGLA,
        portfolioEscalation,
        portfolioEscalationProperties,

        // Allow manual refetch (e.g. "Try Again" button)
        refetch: fetchAllData,
    };
};

export default useExecutiveDashboardData;